import { useEffect, useState } from "react";
import { Grid } from "../../../components/molecules/Grid";
import { PageContent } from "../../../components/organisms/Contents/PageContent";
import { DataFlow } from "../../../components/molecules/DataFlow";
import { Legend } from "../../../components/molecules/Legends";
import { ColDef } from "ag-grid-community";
import { PageContentTitle } from "../../../components/organisms/Contents/PageContent/PageContent.style";
import { useQuery } from "@tanstack/react-query";
import { request } from "../../../common/api";
import {
  DataMonitoringErrorType,
  DataMonitoringType,
} from "../../../common/types/dataMonitoring";
import { useRecoilValue } from "recoil";
import { currentShipState } from "../../../common/states/ship";
import { Buttons } from "../../../components/atoms/Buttons";
import { showAlert } from "../../../components/containers/Alert";
import * as XLSX from "xlsx";
import dayjs from "dayjs";

export const DataMonitoringPage = () => {
  const currentShip = useRecoilValue(currentShipState);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // 첫 번째 useQuery: currentDataMonitoringData
  const { data: currentDataMonitoringData, refetch: refetchCurrentData } =
    useQuery({
      queryKey: ["GET_DATA_MONITORING_DASHBORD"],
      queryFn: async () => {
        return await request<DataMonitoringType>({
          method: "GET",
          url: `/data-monitoring/dashbord`,
          params: { shipNo: currentShip.shipNo },
        });
      },
    });

  // 두 번째 useQuery: errorDataMonitoring
  const { data: errorDataMonitoring, refetch: refetchErrorData } = useQuery({
    queryKey: ["GET_DATA_MONITORING_ERROR"],
    queryFn: async () => {
      return await request<DataMonitoringErrorType[]>({
        method: "GET",
        url: `/data-monitoring`,
        params: { shipNo: currentShip.shipNo },
      });
    },
  });

  // useEffect를 사용하여 30초마다 쿼리를 refetch
  useEffect(() => {
    const intervalId = setInterval(() => {
      refetchCurrentData(); // 첫 번째 쿼리 refetch
      refetchErrorData(); // 두 번째 쿼리 refetch
    }, 30000); // 30초마다 실행

    // 컴포넌트가 언마운트 될 때 setInterval을 정리
    return () => clearInterval(intervalId);
  }, [refetchCurrentData, refetchErrorData]);

  /** Excel 다운로드 */
  const exportToCSV = (data: DataMonitoringErrorType[]) => {
    // 각 열의 Header Name 설정
    const formattedData = data.map((item, idx) => {
      return {
        No: idx + 1,
        에러발생시간: dayjs(item.errorDate).format("YYYY.MM.DD_HH:mm:ss"),
        구분: item.id && "수집",
        에러메시지: item.errormessage,
      };
    });

    // 데이터를 워크시트로 변환
    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    // 워크시트를 CSV로 변환
    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);

    // CSV 문자열에서 Blob 생성 및 다운로드 트리거
    const blob = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    // 파일 이름 설정
    let fileName = `${data[0].id ? "수집" : ""}_에러발생데이터.csv`;

    // 다운로드 트리거
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setIsLoading(false);
  };

  // 그리드 데이터 csv 파일로 다운로드
  const handleDownloadClick = () => {
    setIsLoading(true);
    try {
      if (errorDataMonitoring && errorDataMonitoring.length > 0) {
        exportToCSV(errorDataMonitoring);
      } else {
        setIsLoading(false);
        showAlert("조회된 데이터 결과가 없습니다.");
      }
    } catch (e) {
      console.error(e);
      setIsLoading(false);
    }
  };

  const columnDefs: ColDef[] = [
    {
      headerName: "에러발생 시간",
      field: "errordate",
      flex: 1,
    },
    {
      headerName: "구분",
      cellRenderer: (e: any) => {
        if (e.data.id) {
          return "수집";
        }
      },
      flex: 1,
    },
    {
      headerName: "에러 메시지",
      field: "errormessage",
      flex: 1,
    },
  ];

  return (
    <PageContent
      depth01Title="데이터 통합관리"
      depth02Title="데이터 관리"
      depth03Title="송∙수신 모니터링"
    >
      <>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <PageContentTitle>
            <p>데이터 송∙수신 모니터링</p>
            <Legend title="LED" type="led-monitor" />
          </PageContentTitle>
          <Buttons
            type="button"
            size="md"
            layout="primary"
            label="csv 다운로드"
            onClick={handleDownloadClick}
          />
        </div>
        <DataFlow data={currentDataMonitoringData} />
        <Grid rowData={errorDataMonitoring} columnDefs={columnDefs} />
      </>
    </PageContent>
  );
};
